import React, { useCallback, useEffect, useRef, useState } from "react";
import client from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Database, Image as ImageIcon, Cpu, CheckCircle2, XCircle, Clock,
  Play, Pause, RefreshCw, UploadCloud, Search as SearchIcon, ChevronLeft, ChevronRight, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="border rounded-md p-5 bg-card" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
      <Icon className={`w-4 h-4 ${accent ? "text-brand" : "text-muted-foreground"}`} />
    </div>
    <div className="font-mono text-3xl font-bold mt-3">{value?.toLocaleString?.() ?? value ?? "—"}</div>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [parts, setParts] = useState(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await client.get("/admin/stats");
      setStats(data);
    } catch (e) {
      /* ignore transient */
    }
  }, []);

  const loadParts = useCallback(async () => {
    const { data } = await client.get("/admin/parts", { params: { page, page_size: 15, q: q || undefined } });
    setParts(data);
  }, [page, q]);

  useEffect(() => {
    loadStats();
    const t = setInterval(loadStats, 3000);
    return () => clearInterval(t);
  }, [loadStats]);

  useEffect(() => {
    loadParts();
  }, [loadParts]);

  const action = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) toast.success(msg);
      await loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const importMaster = (file) =>
    action(async () => {
      const fd = new FormData();
      if (file) fd.append("file", file);
      await client.post("/admin/import", fd);
    }, "Master JSON import started");

  const worker = stats?.worker_state;
  const imp = stats?.import_state;
  const total = stats?.total_images || 0;
  const done = stats?.processed_images || 0;
  const failed = stats?.failed_images || 0;
  const remaining = (stats?.pending_images || 0) + (stats?.processing_images || 0);
  const pct = total ? Math.round(((done + failed) / total) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-2">Control Center</div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Database & Ingestion</h1>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && importMaster(e.target.files[0])}
          />
          <Button data-testid="import-master-btn" variant="outline" disabled={busy || imp?.running} onClick={() => importMaster(null)} className="gap-2">
            <UploadCloud className="w-4 h-4" /> Import Master JSON
          </Button>
          <Button data-testid="upload-json-btn" variant="ghost" disabled={busy || imp?.running} onClick={() => fileRef.current?.click()} className="gap-2">
            Upload new JSON
          </Button>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard icon={Database} label="Total Parts" value={stats?.total_parts} accent />
        <StatCard icon={ImageIcon} label="Total Images" value={stats?.total_images} />
        <StatCard icon={Cpu} label="Embeddings" value={stats?.embeddings_created} accent />
        <StatCard icon={CheckCircle2} label="Processed" value={stats?.processed_images} />
        <StatCard icon={Clock} label="Pending" value={stats?.pending_images} />
        <StatCard icon={XCircle} label="Failed" value={stats?.failed_images} />
      </div>

      {/* Ingestion progress */}
      <div className="border rounded-md p-6 bg-card space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className={`inline-flex w-2.5 h-2.5 rounded-full ${worker?.active ? "bg-green-500 animate-pulse" : "bg-muted-foreground/40"}`} />
            <span className="text-sm font-semibold">
              Ingestion worker: <span className={worker?.active ? "text-green-600" : "text-muted-foreground"}>{worker?.active ? "RUNNING" : "IDLE"}</span>
            </span>
            {imp?.running && <span className="text-xs text-muted-foreground font-mono">· import: {imp.phase}</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="ingest-start-btn" size="sm" variant="outline" disabled={busy} onClick={() => action(() => client.post("/admin/ingest/start"), "Worker started")} className="gap-2">
              <Play className="w-4 h-4" /> Start
            </Button>
            <Button data-testid="ingest-stop-btn" size="sm" variant="outline" disabled={busy} onClick={() => action(() => client.post("/admin/ingest/stop"), "Worker stopped")} className="gap-2">
              <Pause className="w-4 h-4" /> Pause
            </Button>
            <Button data-testid="retry-failed-btn" size="sm" variant="outline" disabled={busy} onClick={() => action(() => client.post("/admin/retry-failed"), "Failed images requeued")} className="gap-2">
              <RefreshCw className="w-4 h-4" /> Retry Failed
            </Button>
            <Button data-testid="refeaturize-btn" size="sm" variant="outline" disabled={busy} onClick={() => action(() => client.post("/admin/refeaturize"), "Re-featurization queued")} className="gap-2">
              <Cpu className="w-4 h-4" /> Re-featurize
            </Button>
            <Button data-testid="reindex-btn" size="sm" variant="ghost" disabled={busy} onClick={() => action(() => client.post("/admin/reindex"), "Search index rebuilt")} className="gap-2">
              Reindex
            </Button>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between text-xs font-mono mb-2">
            <span>Processed: {(done + failed).toLocaleString()} / {total.toLocaleString()}</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs font-mono mt-3 text-muted-foreground">
            <span>Successful: <span className="text-green-600">{done.toLocaleString()}</span></span>
            <span>Failed: <span className="text-brand">{failed.toLocaleString()}</span></span>
            <span>Remaining: {remaining.toLocaleString()}</span>
            <span>Index size: {stats?.index_size?.toLocaleString?.() ?? 0}</span>
            <span>DINO: {stats?.dino_embeddings?.toLocaleString?.() ?? 0}</span>
            <span>OCR-indexed: {stats?.ocr_indexed?.toLocaleString?.() ?? 0}</span>
            <span>Feature v{stats?.feature_version ?? 2}: {stats?.feature_v2_images?.toLocaleString?.() ?? 0}</span>
          </div>
        </div>
      </div>

      {/* Parts table */}
      <div className="border rounded-md bg-card">
        <div className="flex items-center justify-between p-4 border-b gap-4">
          <h2 className="font-heading font-bold text-lg">Parts Index</h2>
          <div className="relative w-full max-w-xs">
            <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="parts-search"
              placeholder="Search Part No…"
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
              className="pl-9 h-9 font-mono text-sm"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b">
                <th className="px-4 py-3">Part No</th>
                <th className="px-4 py-3">Images</th>
                <th className="px-4 py-3">Embedded</th>
                <th className="px-4 py-3">Failed</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {!parts ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
              ) : parts.parts.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No parts found</td></tr>
              ) : (
                parts.parts.map((p) => (
                  <tr key={p.id} className="border-b last:border-0 hover:bg-secondary/40" data-testid={`parts-row-${p.part_no}`}>
                    <td className="px-4 py-3 font-mono font-medium">{p.part_no}</td>
                    <td className="px-4 py-3 font-mono">{p.image_count}</td>
                    <td className="px-4 py-3 font-mono text-green-600">{p.done_images}</td>
                    <td className="px-4 py-3 font-mono text-brand">{p.failed_images}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${p.done_images > 0 ? "bg-green-100 text-green-700" : "bg-secondary text-muted-foreground"}`}>
                        {p.done_images > 0 ? "Indexed" : "Pending"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {parts && (
          <div className="flex items-center justify-between p-4 border-t text-sm">
            <span className="text-muted-foreground font-mono text-xs">
              {parts.total.toLocaleString()} parts · page {parts.page}
            </span>
            <div className="flex items-center gap-2">
              <Button data-testid="prev-page" size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <Button data-testid="next-page" size="sm" variant="outline" disabled={parts.page * parts.page_size >= parts.total} onClick={() => setPage((p) => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
